/* eslint-disable */
import { TBusiness } from '@/context/types/Business';
import { NotificationType } from '@/context/types/Notification';
import useAuth from '@/hooks/useAuth';
import useHomeContext from '@/hooks/useHomeContext';
import useXero from '@/hooks/useXero';
import { IGetXeroTenantResponse, XeroConnectionStateEnum } from '@/services/types/Xero';
import {
  createXeroConnection,
  createXeroTenantConnection,
  getTenant,
  getXeroTenants
} from '@/services/xero';
import { XERO_TENANT_SESSION_STORAGE_NAME } from '@/utils/browserStorageNames';
import { routes } from '@/utils/routes';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const useXeroConnection = () => {
  const navigate = useNavigate();
  const { isLoggedIn, accountId, customer, business } = useAuth()!;
  const {
    setXeroTenant,
    setIsXeroConnected,
    setIsSelectingFeed,
    setIsFirstConnection,
    setHasExistingConnection,
    setXeroConnection,
    setIsXeroAuthoriser,
    setHasMissingScope,
    setXeroAuthoriserName
  } = useXero()!;
  const { triggerNotification } = useHomeContext()!;

  const [xeroTenants, setXeroTenants] = useState<IGetXeroTenantResponse[]>([]);
  const [openXeroTenantsModal, setOpenXeroTenantsModal] = useState(false);
  const [initialLoadingState, setInitialLoadingState] = useState(true);

  useEffect(() => {
    if (isLoggedIn && accountId && customer && business) {
      const url = new URL(window.location.href);
      const searchParams = url.searchParams;

      // scenario when redirected back to Emerge after logging in in Xero
      if (searchParams.has('state') && searchParams.has('code')) {
        const handleCreateTenantConnection = async (connectionId: string, tenant: IGetXeroTenantResponse) => {
          let tempTenant = Object.assign({}, tenant);
          
          // first time connecting to Xero
          if (!tenant?.accountId) {
            setIsSelectingFeed(true);
            tempTenant = (await createXeroTenantConnection({ connectionId, tenantId: tenant.id }))
              .data;
          }
  
          setHasExistingConnection(true);
          setXeroTenant(tempTenant);
        };

        const startConnectionWithXero = async (code: string, secret: string) => {
          const createdConnection = await createXeroConnection({ code, secret });
          const connectionId = createdConnection.data.id;
          setIsXeroConnected(createdConnection.data.state == XeroConnectionStateEnum.ACTIVE);
          
          setIsXeroAuthoriser(true)
          setXeroAuthoriserName(customer.preferredName ?? customer.name)
          
          if (createdConnection.data.state === XeroConnectionStateEnum.MISSING_SCOPE) {
            triggerNotification(NotificationType.REAUTHENTICATION_REQUIRED);
            setHasMissingScope(true)
          }
          
          const res = await getXeroTenants(connectionId);
          const foundTenants = res.data;
          const sessionTenant = sessionStorage.getItem(XERO_TENANT_SESSION_STORAGE_NAME);

          if (foundTenants.length === 1) {
            const singleTenant = foundTenants[0]

            if (!isTenantAvailableForConnection(singleTenant) && !isTenantUnderCurrentBusiness(singleTenant, business)) {
              triggerNotification(NotificationType.NO_AVAILABLE_XERO_ORGANIZATION)
              sessionStorage.removeItem(XERO_TENANT_SESSION_STORAGE_NAME)
              setXeroTenant(undefined)
              setHasExistingConnection(false)
              setIsXeroConnected(false)
              return;
            }

            await handleCreateTenantConnection(connectionId, singleTenant);
            if (singleTenant.id != sessionTenant) {
              sessionStorage.setItem(XERO_TENANT_SESSION_STORAGE_NAME, singleTenant.id);
              setIsSelectingFeed(true);
              setIsFirstConnection(true);
              setHasExistingConnection(true);
              triggerNotification(NotificationType.CONNECT_SUCCESS);
              navigate(routes.xero);
            }
          } else {
            // In case there are several tenants under the connection, we need to check first if there is a tenant under the business
            const existingConnectedTenant = foundTenants.find((el) => isTenantUnderCurrentBusiness(el, business));
            if (existingConnectedTenant) {
              // If they fall under this condition, it means that the user only reconnected to get new access and refresh token from Xero
              sessionStorage.setItem(XERO_TENANT_SESSION_STORAGE_NAME, existingConnectedTenant.id);
              setXeroTenant(existingConnectedTenant);
              setHasExistingConnection(true);
              triggerNotification(NotificationType.CONNECT_SUCCESS);
              return;
            }

            // If no existing tenants under the business, it is their first time connecting a tenant to the Xero connection
            const availableTenants = foundTenants.filter((tenant) => isTenantAvailableForConnection(tenant));

            if (availableTenants.length === 1) {
              // Automatically connect the available tenant if there's only one available
              await handleCreateTenantConnection(connectionId, availableTenants[0]);
              sessionStorage.setItem(XERO_TENANT_SESSION_STORAGE_NAME, availableTenants[0].id);
              setIsSelectingFeed(true);
              setIsFirstConnection(true);
              setHasExistingConnection(true);
              triggerNotification(NotificationType.CONNECT_SUCCESS);
              navigate(routes.xero);
              return;
            } else if(availableTenants.length > 1) {
              // Multiple tenants available, let the user choose which one to connect
              setXeroConnection(connectionId);
              setXeroTenants(availableTenants);
              setOpenXeroTenantsModal(true);
            } else {
              // All of the tenants under the Xero connection are all connected to a business
              // This is not an ideal scenario but it could happen if they did not choose an organization on the Xero auth page
              // in this case, the user needs to reconnect again so they can choose an organization
              sessionStorage.removeItem(XERO_TENANT_SESSION_STORAGE_NAME)
              setXeroTenant(undefined)
              setHasExistingConnection(false)
              setIsXeroConnected(false)
              triggerNotification(NotificationType.NO_AVAILABLE_XERO_ORGANIZATION)
            }
          }
        };

        const code = searchParams.get('code')!;
        const secret = searchParams.get('state')!;

        startConnectionWithXero(code, secret);
        setInitialLoadingState(false);
        return;
      }

      const queryConnectionTenant = async () => {
        try {
          const tenantRes = await getTenant();

          if (!tenantRes.data) {
            setInitialLoadingState(false);
            setHasExistingConnection(false);
            sessionStorage.removeItem(XERO_TENANT_SESSION_STORAGE_NAME);
            return;
          }

          const tenant = tenantRes.data;
          sessionStorage.setItem(XERO_TENANT_SESSION_STORAGE_NAME, tenant.tenantId);
        
          setIsXeroConnected(tenant.state === XeroConnectionStateEnum.ACTIVE);
          setIsXeroAuthoriser(tenant.customerId === customer.id);
          setXeroAuthoriserName(tenant.customerName)
          
          if (tenant.state === XeroConnectionStateEnum.ACCESS_REVOKED) {
            triggerNotification(NotificationType.LOST_CONNECTION);
          }

          if (tenant.state === XeroConnectionStateEnum.MISSING_SCOPE) {
            triggerNotification(NotificationType.REAUTHENTICATION_REQUIRED);
            setHasMissingScope(true)
          }

          setHasExistingConnection(true);
          setXeroTenant({
            id: tenant.tenantId,
            name: tenant.tenantName,
            accountId
          });
          setInitialLoadingState(false);
        } catch (err) {
          setInitialLoadingState(false);
        }
      };

      queryConnectionTenant();
    }
  }, [isLoggedIn, accountId, customer, business]);

  return {
    xeroTenants,
    openXeroTenantsModal,
    initialLoadingState,
    setOpenXeroTenantsModal,
    setIsXeroConnected,
    setInitialLoadingState
  };
};

const isTenantAvailableForConnection = (tenant: IGetXeroTenantResponse) => !tenant.accountId

const isTenantUnderCurrentBusiness = (
  tenant: IGetXeroTenantResponse, 
  business: TBusiness | undefined
) => tenant.accountId && tenant.businessId && tenant.businessId === business?.id
